<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref, nextTick } from 'vue';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedPipeline = ref({});

const pipelineName = ref('');
const pipelineStages = ref([]);

const COLORS = [
  '#1f93ff', '#22c55e', '#f59e0b', '#ef4444', '#a855f7',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#64748b',
];

const records = computed(() => getters['pipelines/getPipelines'].value);
const uiFlags = computed(() => getters['pipelines/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedPipeline.value.name}?`);

function getNextColor() {
  const usedColors = pipelineStages.value.map(s => s.color);
  return COLORS.find(c => !usedColors.includes(c)) || COLORS[0];
}

function resetForm() {
  pipelineName.value = '';
  pipelineStages.value = [
    { name: '', color: COLORS[0], position: 0 },
    { name: '', color: COLORS[1], position: 1 },
  ];
}

function addStage() {
  pipelineStages.value.push({
    name: '',
    color: getNextColor(),
    position: pipelineStages.value.length,
  });
  nextTick(() => {
    const inputs = document.querySelectorAll('.stage-name-input');
    if (inputs.length) inputs[inputs.length - 1].focus();
  });
}

function removeStage(index) {
  if (pipelineStages.value.length <= 1) return;
  pipelineStages.value.splice(index, 1);
  pipelineStages.value.forEach((s, i) => { s.position = i; });
}

function openAddPopup() {
  resetForm();
  showAddPopup.value = true;
}
function hideAddPopup() {
  showAddPopup.value = false;
}

function openEditPopup(pipeline) {
  selectedPipeline.value = pipeline;
  pipelineName.value = pipeline.name;
  pipelineStages.value = (pipeline.pipeline_stages || []).map(s => ({
    id: s.id,
    name: s.name,
    color: s.color,
    position: s.position,
  }));
  if (!pipelineStages.value.length) {
    pipelineStages.value = [{ name: '', color: COLORS[0], position: 0 }];
  }
  showEditPopup.value = true;
}
function hideEditPopup() {
  showEditPopup.value = false;
}

function openDeletePopup(pipeline) {
  showDeleteConfirmationPopup.value = true;
  selectedPipeline.value = pipeline;
}
function closeDeletePopup() {
  showDeleteConfirmationPopup.value = false;
}

const isFormValid = computed(() => {
  return (
    pipelineName.value.trim() &&
    pipelineStages.value.length > 0 &&
    pipelineStages.value.every(s => s.name.trim())
  );
});

async function createPipeline() {
  if (!isFormValid.value) return;
  try {
    await store.dispatch('pipelines/create', {
      name: pipelineName.value.trim(),
      pipeline_stages_attributes: pipelineStages.value.map((s, i) => ({
        name: s.name.trim(),
        color: s.color,
        position: i,
      })),
    });
    useAlert('Pipeline criado com sucesso');
    hideAddPopup();
  } catch (error) {
    useAlert(error?.message || 'Erro ao criar pipeline');
  }
}

async function updatePipeline() {
  if (!isFormValid.value) return;
  const existingIds = (selectedPipeline.value.pipeline_stages || []).map(s => s.id);
  const currentIds = pipelineStages.value.filter(s => s.id).map(s => s.id);
  const removedIds = existingIds.filter(id => !currentIds.includes(id));

  const stagesAttrs = pipelineStages.value.map((s, i) => ({
    ...(s.id ? { id: s.id } : {}),
    name: s.name.trim(),
    color: s.color,
    position: i,
  }));

  removedIds.forEach(id => {
    stagesAttrs.push({ id, _destroy: true });
  });

  try {
    await store.dispatch('pipelines/update', {
      id: selectedPipeline.value.id,
      name: pipelineName.value.trim(),
      pipeline_stages_attributes: stagesAttrs,
    });
    useAlert('Pipeline atualizado com sucesso');
    hideEditPopup();
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar pipeline');
  }
}

async function deletePipeline() {
  try {
    await store.dispatch('pipelines/delete', selectedPipeline.value.id);
    useAlert('Pipeline removido com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover pipeline');
  } finally {
    loading.value[selectedPipeline.value.id] = false;
  }
}

function confirmDeletion() {
  loading.value[selectedPipeline.value.id] = true;
  closeDeletePopup();
  deletePipeline();
}

const tableHeaders = ['Nome', 'Etapas', 'Ações'];

onBeforeMount(() => {
  store.dispatch('pipelines/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    loading-message="Carregando pipelines..."
    :no-records-found="!records.length"
    no-records-message="Nenhum pipeline encontrado. Crie o primeiro!"
  >
    <template #header>
      <BaseSettingsHeader
        title="Pipeline"
        description="Crie e gerencie funis com etapas personalizadas para organizar suas conversas."
        feature-name="pipelines"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ records.length }} {{ records.length === 1 ? 'pipeline' : 'pipelines' }}
          </span>
        </template>
        <template #actions>
          <Button label="Novo Pipeline" size="sm" @click="openAddPopup" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        no-data-message="Nenhum pipeline encontrado."
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="pipeline in items"
            :key="pipeline.id"
            :item="pipeline"
          >
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 font-medium">
                  {{ pipeline.name }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <div class="flex items-center gap-1.5 flex-wrap">
                  <span
                    v-for="stage in pipeline.pipeline_stages"
                    :key="stage.id"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    :style="{
                      backgroundColor: stage.color + '20',
                      color: stage.color,
                    }"
                  >
                    <span
                      class="w-2 h-2 rounded-full"
                      :style="{ backgroundColor: stage.color }"
                    />
                    {{ stage.name }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="'Editar'"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[pipeline.id]"
                    @click="openEditPopup(pipeline)"
                  />
                  <Button
                    v-tooltip.top="'Excluir'"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[pipeline.id]"
                    @click="openDeletePopup(pipeline)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <!-- ADD MODAL -->
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          header-title="Novo Pipeline"
          header-content="Crie um funil com etapas personalizadas."
        />
        <form class="flex flex-col gap-4 mx-0 px-8 pb-8" @submit.prevent="createPipeline">
          <woot-input
            v-model="pipelineName"
            class="w-full"
            label="Nome do Pipeline"
            placeholder="Ex: Funil de vendas"
          />

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Etapas do funil
            </label>
            <div class="flex flex-col gap-2">
              <div
                v-for="(stage, index) in pipelineStages"
                :key="index"
                class="flex items-center gap-2"
              >
                <span class="text-xs text-n-slate-10 w-5 text-center select-none">
                  {{ index + 1 }}
                </span>
                <span
                  class="w-5 h-5 rounded-full border border-n-weak flex-shrink-0 cursor-pointer"
                  :style="{ backgroundColor: stage.color }"
                  @click="stage.color = COLORS[(COLORS.indexOf(stage.color) + 1) % COLORS.length]"
                />
                <input
                  v-model="stage.name"
                  class="stage-name-input flex-1 px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-n-brand transition-colors"
                  :placeholder="'Etapa ' + (index + 1)"
                  @keydown.enter.prevent="addStage"
                />
                <button
                  v-if="pipelineStages.length > 1"
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-2 transition-colors"
                  @click="removeStage(index)"
                >
                  <span class="i-lucide-x w-4 h-4" />
                </button>
                <div v-else class="w-7" />
              </div>
            </div>
            <button
              type="button"
              class="mt-2 text-sm text-n-brand hover:underline flex items-center gap-1"
              @click="addStage"
            >
              <span class="i-lucide-plus w-4 h-4" />
              Adicionar etapa
            </button>
          </div>

          <div class="flex items-center justify-end w-full gap-2 pt-2">
            <Button
              faded
              slate
              type="reset"
              label="Cancelar"
              @click.prevent="hideAddPopup"
            />
            <Button
              type="submit"
              label="Criar Pipeline"
              :disabled="!isFormValid || uiFlags.isCreating"
              :is-loading="uiFlags.isCreating"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- EDIT MODAL -->
    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          header-title="Editar Pipeline"
          header-content="Altere o nome e as etapas do funil."
        />
        <form class="flex flex-col gap-4 mx-0 px-8 pb-8" @submit.prevent="updatePipeline">
          <woot-input
            v-model="pipelineName"
            class="w-full"
            label="Nome do Pipeline"
            placeholder="Ex: Funil de vendas"
          />

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Etapas do funil
            </label>
            <div class="flex flex-col gap-2">
              <div
                v-for="(stage, index) in pipelineStages"
                :key="stage.id || 'new-' + index"
                class="flex items-center gap-2"
              >
                <span class="text-xs text-n-slate-10 w-5 text-center select-none">
                  {{ index + 1 }}
                </span>
                <span
                  class="w-5 h-5 rounded-full border border-n-weak flex-shrink-0 cursor-pointer"
                  :style="{ backgroundColor: stage.color }"
                  @click="stage.color = COLORS[(COLORS.indexOf(stage.color) + 1) % COLORS.length]"
                />
                <input
                  v-model="stage.name"
                  class="stage-name-input flex-1 px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-n-brand transition-colors"
                  :placeholder="'Etapa ' + (index + 1)"
                  @keydown.enter.prevent="addStage"
                />
                <button
                  v-if="pipelineStages.length > 1"
                  type="button"
                  class="w-7 h-7 flex items-center justify-center rounded-md text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-2 transition-colors"
                  @click="removeStage(index)"
                >
                  <span class="i-lucide-x w-4 h-4" />
                </button>
                <div v-else class="w-7" />
              </div>
            </div>
            <button
              type="button"
              class="mt-2 text-sm text-n-brand hover:underline flex items-center gap-1"
              @click="addStage"
            >
              <span class="i-lucide-plus w-4 h-4" />
              Adicionar etapa
            </button>
          </div>

          <div class="flex items-center justify-end w-full gap-2 pt-2">
            <Button
              faded
              slate
              type="reset"
              label="Cancelar"
              @click.prevent="hideEditPopup"
            />
            <Button
              type="submit"
              label="Salvar"
              :disabled="!isFormValid || uiFlags.isUpdating"
              :is-loading="uiFlags.isUpdating"
            />
          </div>
        </form>
      </div>
    </woot-modal>

    <!-- DELETE MODAL -->
    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      title="Excluir pipeline"
      message="Tem certeza que deseja excluir o pipeline"
      :message-value="deleteMessage"
      confirm-text="Sim, excluir"
      reject-text="Não, cancelar"
    />
  </SettingsLayout>
</template>
